import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { UserRole } from '@/types';
import { PasswordRegisterForm, PasswordRegisterFormValues } from './forms/PasswordRegisterForm';
import { SeedphraseRegisterForm, SeedphraseRegisterFormValues } from './forms/SeedphraseRegisterForm';
import { SeedphraseDisplay } from './forms/SeedphraseDisplay';

const RegisterForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { register: authRegister, registerWithSeedPhrase } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [generatedSeedphrase, setGeneratedSeedphrase] = useState<string | null>(null);
  const navigate = useNavigate();

  const onPasswordSubmit = async (data: PasswordRegisterFormValues) => {
    setIsLoading(true);
    try {
      console.log('Registration with password:', data);
      
      // Ensure displayName is included in the user metadata
      await authRegister(
        data.email, 
        data.password, 
        data.role as UserRole, 
        { displayName: data.name }
      );
      
      toast({
        title: "Registration successful",
        description: "Your account has been created!",
      });
      // Redirect to dashboard after successful registration
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration failed",
        description: (error instanceof Error && error.message) || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSeedphraseSubmit = async (data: SeedphraseRegisterFormValues) => {
    setIsLoading(true);
    try {
      console.log('Registration with seedphrase:', data);
      
      // Pass display name to seedphrase registration
      const result = await registerWithSeedPhrase(
        data.role as UserRole,
        { displayName: data.name }
      );
      
      setGeneratedSeedphrase(result.seedPhrase);
      toast({
        title: "Registration successful",
        description: "Your account has been created with a seedphrase!",
      });
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration failed",
        description: (error instanceof Error && error.message) || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueAfterSeedphrase = () => {
    navigate('/dashboard');
  };

  return (
    <div className="w-full max-w-md p-6 bg-card border border-border rounded-lg shadow-md">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold mb-2">{t('Register')}</h1>
        <p className="text-muted-foreground">{t('Create your account')}</p>
      </div>
      
      {generatedSeedphrase ? (
        <SeedphraseDisplay 
          seedPhrase={generatedSeedphrase}
          onContinue={handleContinueAfterSeedphrase}
        />
      ) : (
        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="email">{t('Email & Password')}</TabsTrigger>
            <TabsTrigger value="seedphrase">{t('Anonymous (Seedphrase)')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="email" className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('Create an account with your email')}</p>
            <PasswordRegisterForm 
              onSubmit={onPasswordSubmit}
              isLoading={isLoading}
            />
          </TabsContent>
          
          <TabsContent value="seedphrase" className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('Create an anonymous account with a seedphrase')}</p>
            <SeedphraseRegisterForm 
              onSubmit={onSeedphraseSubmit}
              isLoading={isLoading}
            />
          </TabsContent>
        </Tabs>
      )}
      
      {!generatedSeedphrase && (
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('Already have an account?')}{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t('Sign in')}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
